import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  User as FirebaseUser,
  updateProfile,
} from 'firebase/auth';
import { auth, db } from '../config/firebase';
import { doc, setDoc, getDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { Organization, User, Role, Invitation } from '@easydone/shared';
import { useInvitation, getInvitationByToken } from './invitationService';
import { timestampToMillis } from '../utils/timestamp';

export interface RegisterData {
  email: string;
  password: string;
  displayName: string;
  organizationName: string;
}

/**
 * Register a new user and create their organization
 */
export async function registerUser(data: RegisterData): Promise<FirebaseUser> {
  // 1. Create Firebase Auth user
  const userCredential = await createUserWithEmailAndPassword(
    auth,
    data.email,
    data.password
  );

  const firebaseUser = userCredential.user;

  try {
    // 2. Update display name
    await updateProfile(firebaseUser, {
      displayName: data.displayName,
    });

    // 3. Create organization
    const orgId = doc(db, 'organizations', firebaseUser.uid).id; // Use user ID as org ID for simplicity
    const organization: Organization = {
      id: orgId,
      name: data.organizationName,
      tier: 'starter',
      billingEmail: data.email,
      subscriptionStatus: 'trial',
      trialEndsAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days trial
      settings: {
        branding: {
          primaryColor: '#3B82F6',
          companyName: data.organizationName,
        },
        features: {
          ocrEnabled: false,
          apiAccessEnabled: false,
          ssoEnabled: false,
          customIntegrations: false,
        },
        limits: {
          maxUsers: 10,
          maxSites: 50,
          maxStorageGB: 10,
          ocrCreditsPerMonth: 100,
        },
        notifications: {
          inspectionCompleted: true,
          inspectionOverdue: true,
          calibrationDue: true,
        },
      },
      createdAt: serverTimestamp() as any,
      createdBy: firebaseUser.uid,
      updatedAt: serverTimestamp() as any,
      industry: 'water_utility',
    };

    await setDoc(doc(db, 'organizations', orgId), organization);

    // 4. Create user profile
    const userProfile: User = {
      id: firebaseUser.uid,
      email: data.email,
      displayName: data.displayName,
      orgId: orgId,
      role: Role.ORG_ADMIN, // First user is always admin
      permissions: ['*'], // Admin has all permissions
      status: 'active',
      preferences: {
        language: 'en',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        notifications: {
          email: true,
          push: true,
        },
      },
      createdAt: serverTimestamp() as any,
    };

    await setDoc(doc(db, 'users', firebaseUser.uid), userProfile);

    return firebaseUser;
  } catch (error) {
    // If anything fails, delete the auth user
    await firebaseUser.delete();
    throw error;
  }
}

/**
 * Sign in existing user
 */
export async function signIn(email: string, password: string): Promise<FirebaseUser> {
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  return userCredential.user;
}

/**
 * Sign out current user
 */
export async function signOut(): Promise<void> {
  await firebaseSignOut(auth);
}

/**
 * Get user profile from Firestore
 */
export async function getUserProfile(userId: string): Promise<User | null> {
  const userDoc = await getDoc(doc(db, 'users', userId));
  if (!userDoc.exists()) {
    return null;
  }
  return userDoc.data() as User;
}

/**
 * Get organization by ID
 */
export async function getOrganization(orgId: string): Promise<Organization | null> {
  const orgDoc = await getDoc(doc(db, 'organizations', orgId));
  if (!orgDoc.exists()) {
    return null;
  }
  return orgDoc.data() as Organization;
}

/**
 * Register a new user with an invitation token
 */
export async function registerWithInvitation(
  email: string,
  password: string,
  displayName: string,
  invitationToken: string
): Promise<{ firebaseUser: FirebaseUser; invitation: Invitation }> {
  // 1. Validate invitation first (before creating auth user)
  const invitation = await getInvitationByToken(invitationToken);

  if (!invitation) {
    throw new Error('Invitation not found');
  }

  if (!invitation.isActive) {
    throw new Error('Invitation has been revoked');
  }

  const now = Date.now();
  const expiresAt = timestampToMillis(invitation.expiresAt);

  if (expiresAt < now) {
    throw new Error('Invitation has expired');
  }

  if (invitation.maxUses && invitation.usedCount >= invitation.maxUses) {
    throw new Error('Invitation has reached maximum uses');
  }

  // 2. Create Firebase Auth user
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  const firebaseUser = userCredential.user;

  try {
    // 3. Update display name
    await updateProfile(firebaseUser, { displayName });

    // 4. Create user profile with invitation's role and org
    const userProfile: User = {
      id: firebaseUser.uid,
      email,
      displayName,
      orgId: invitation.orgId,
      role: invitation.role,
      permissions: [], // Permissions based on role
      status: 'active',
      preferences: {
        language: 'en',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        notifications: {
          email: true,
          push: true,
        },
      },
      createdAt: serverTimestamp() as any,
    };

    await setDoc(doc(db, 'users', firebaseUser.uid), userProfile);

    // 5. Mark invitation as used
    const invitationResult = await useInvitation(invitationToken, firebaseUser.uid);

    if (!invitationResult.valid || !invitationResult.invitation) {
      // This shouldn't happen since we validated earlier, but handle it
      throw new Error(invitationResult.error || 'Failed to use invitation');
    }

    return { firebaseUser, invitation: invitationResult.invitation };
  } catch (error) {
    // If anything fails, delete the auth user
    await firebaseUser.delete();
    throw error;
  }
}

/**
 * Join an additional organization using an invitation token (for existing users)
 */
export async function joinOrganizationWithInvitation(
  userId: string,
  invitationToken: string
): Promise<Invitation> {
  // 1. Get current user profile
  let userProfile = await getUserProfile(userId);

  // 2. Use the invitation
  const invitationResult = await useInvitation(invitationToken, userId);

  if (!invitationResult.valid || !invitationResult.invitation) {
    throw new Error(invitationResult.error || 'Invalid invitation');
  }

  const invitation = invitationResult.invitation;

  // 3. If user profile doesn't exist, create it
  if (!userProfile) {
    // Get user info from Firebase Auth
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      throw new Error('User not authenticated');
    }

    const newUserProfile: User = {
      id: userId,
      email: firebaseUser.email || '',
      displayName: firebaseUser.displayName || firebaseUser.email || 'User',
      orgId: invitation.orgId,
      role: invitation.role,
      permissions: [],
      status: 'active',
      preferences: {
        language: 'en',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        notifications: {
          email: true,
          push: true,
        },
      },
      createdAt: serverTimestamp() as any,
    };

    await setDoc(doc(db, 'users', userId), newUserProfile);
    return invitation;
  }

  // 4. Check if user is already in this organization
  if (userProfile.orgId === invitation.orgId) {
    throw new Error('You are already a member of this organization');
  }

  // 5. Update user's organization and role
  await updateDoc(doc(db, 'users', userId), {
    orgId: invitation.orgId,
    role: invitation.role,
    updatedAt: serverTimestamp(),
  });

  return invitation;
}
